import { create } from 'zustand';
import { apiClient } from '~/lib/apiClient';
import { secureStorage } from '~/lib/secureStorage';
import {
  mapSettingsToUser,
  resolveAccessibleTeams,
  type SettingsProfile,
} from '~/lib/teamAccess';
import type { Team } from './useTeamStore';

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  id: number;
  isSuperAdmin?: boolean;
}

interface LoginResponse {
  access_token?: string;
  expires_in?: number;
  token_type?: string;
  detail?: string;
}

interface RegisterResponse {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  detail?: string;
}

interface VerificationResponse {
  message?: string;
  detail?: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  teamName: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  teams: Team[];
  activeTeamId: number | null;
  loading: boolean;
  error: string | null;

  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;

  requestPasswordReset: (email?: string) => Promise<void>;
  resetPassword: (
    token: string,
    password: string,
    email: string,
  ) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;

  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;

  acceptInvitation: (
    teamId: string,
    token: string,
    userData: { firstName: string; lastName: string; password: string },
  ) => Promise<void>;
  checkInvitationStatus: (token: string) => Promise<{ expired?: string }>;
  handleInvitation: (
    teamId: string,
    token: string,
    status: 'accepted' | 'declined',
  ) => Promise<{ message?: string }>;

  switchTeam: (teamId: number, teamsToValidateAgainst?: Team[]) => void;
  refreshTeams: () => Promise<Team[]>;
  createTeam: (data: { name: string }) => Promise<Team>;

  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setTeams: (teams: Team[]) => void;
  setActiveTeamId: (teamId: number | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  _saveAuthData: (authToken: string, userData: User, teamId?: string) => void;
  _clearAuthData: () => void;
  _selectValidTeamId: (
    userTeams?: Team[],
    preferredTeamId?: string,
  ) => string | undefined;
  _refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  token: null,
  teams: [],
  activeTeamId: null,
  loading: true,
  error: null,
  isAuthenticated: false,

  _selectValidTeamId: (userTeams?: Team[], preferredTeamId?: string) => {
        if (!userTeams?.length) return undefined;

        if (preferredTeamId) {
          const teamExists = userTeams.some(
            (team) => team.id.toString() === preferredTeamId,
          );
          if (teamExists) return preferredTeamId;
        }

        return userTeams[0]?.id?.toString();
      },

      _saveAuthData: (authToken: string, userData: User, teamId?: string) => {
        secureStorage.setItem('auth_token', authToken);
        if (teamId) {
          secureStorage.setItem('team_id', teamId);
        }

        set({
          token: authToken,
          user: userData,
          isAuthenticated: true,
        });

        apiClient.setAuth(authToken, teamId);
      },

      _clearAuthData: () => {
        secureStorage.clear();

        set({
          token: null,
          user: null,
          teams: [],
          activeTeamId: null,
          isAuthenticated: false,
        });

        apiClient.clearAuth();
      },

      _refreshUser: async () => {
        try {
          set({ loading: true, error: null });

          const userProfile = await apiClient.get<SettingsProfile>('/settings');
          const userData = mapSettingsToUser(userProfile);
          const teams = await resolveAccessibleTeams();

          const persistedTeamId = secureStorage.getItem('team_id');
          const teamId = get()._selectValidTeamId(
            teams,
            persistedTeamId || undefined,
          );

          set({
            user: userData,
            teams,
            activeTeamId: teamId ? parseInt(teamId) : null,
          });

          if (teamId) {
            secureStorage.setItem('team_id', teamId);
            apiClient.updateTeamId(teamId);
          }
        } catch (err) {
          set({
            error:
              err instanceof Error
                ? err.message
                : 'Failed to refresh user data',
          });

          get()._clearAuthData();
        } finally {
          set({ loading: false });
        }
      },

      login: async (email: string, password: string) => {
        try {
          set({ loading: true, error: null });

          const payload = {
            username: email,
            password,
          };

          const data = await apiClient.post<LoginResponse>(
            '/auth/login',
            payload,
            'application/x-www-form-urlencoded',
          );

          if (data.detail) {
            throw new Error(data.detail);
          }

          if (!data.access_token) {
            throw new Error('Invalid login response');
          }

          apiClient.setAuth(data.access_token);

          const userProfile = await apiClient.get<SettingsProfile>('/settings');
          const userData = mapSettingsToUser(userProfile);
          const teams = await resolveAccessibleTeams();

          const persistedTeamId = secureStorage.getItem('team_id');
          const teamId = get()._selectValidTeamId(
            teams,
            persistedTeamId || undefined,
          );

          set({
            teams,
            activeTeamId: teamId ? parseInt(teamId) : null,
          });

          get()._saveAuthData(data.access_token, userData, teamId);
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Login failed';
          set({ error: errorMessage });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ loading: true, error: null });

          const response = await apiClient.post<RegisterResponse>(
            '/auth/register',
            {
              first_name: data.firstName,
              last_name: data.lastName,
              email: data.email,
              password: data.password,
              team_name: data.teamName,
            },
          );

          if (!response.email) {
            throw new Error(response?.detail || 'Registration failed');
          }
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Registration failed';
          set({ error: errorMessage });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      logout: async () => {
        get()._clearAuthData();
      },

      updateProfile: async (data: Partial<User>) => {
        try {
          set({ error: null });

          await apiClient.patch<User>('/settings', {
            first_name: data.firstName,
            last_name: data.lastName,
          });

          set({
            user: {
              ...get().user,
              firstName: data.firstName || get().user?.firstName || '',
              lastName: data.lastName || get().user?.lastName || '',
            } as User
          });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to update profile';
          set({ error: errorMessage });
          throw err;
        }
      },

      requestPasswordReset: async (email?: string) => {
        try {
          set({ error: null });
          const emailToUse = email || get().user?.email;
          if (!emailToUse) {
            throw new Error('Email is required');
          }

          await apiClient.post<VerificationResponse>('/auth/forgot-password', {
            email: emailToUse,
          });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to send reset email';
          set({ error: errorMessage });
          throw err;
        }
      },

      resetPassword: async (token: string, password: string, email: string) => {
        try {
          set({ error: null });

          await apiClient.put<VerificationResponse>('/auth/reset-password', {
            token,
            password,
            email,
          });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to reset password';
          set({ error: errorMessage });
          throw err;
        }
      },

      forgotPassword: async (email: string) => {
        try {
          set({ error: null });

          await apiClient.post<VerificationResponse>('/auth/forgot-password', {
            email,
          });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to send reset email';
          set({ error: errorMessage });
          throw err;
        }
      },

      verifyEmail: async (email: string, code: string) => {
        try {
          set({ error: null });

          await apiClient.post<VerificationResponse>('/auth/verify', {
            email,
            token: code,
          });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Email verification failed';
          set({ error: errorMessage });
          throw err;
        }
      },

      resendVerification: async (email: string) => {
        try {
          set({ error: null });

          await apiClient.post<VerificationResponse>(
            '/auth/resend-verification',
            {
              email,
            },
          );
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'Failed to resend verification';
          set({ error: errorMessage });
          throw err;
        }
      },

      acceptInvitation: async (
        teamId: string,
        token: string,
        userData: { firstName: string; lastName: string; password: string },
      ) => {
        try {
          set({ loading: true, error: null });

          const data = await apiClient.post<LoginResponse>(
            `/auth/teams/${teamId}/invitation/${token}`,
            {
              first_name: userData.firstName,
              last_name: userData.lastName,
              password: userData.password,
            },
          );

          if (data.detail) {
            throw new Error(data.detail);
          }

          if (!data.access_token) {
            throw new Error('Invalid invitation response');
          }

          apiClient.setAuth(data.access_token);

          const userProfile = await apiClient.get<SettingsProfile>('/settings');
          const authUserData = mapSettingsToUser(userProfile);
          const teams = await resolveAccessibleTeams();

          const persistedTeamId = secureStorage.getItem('team_id');
          const finalTeamId = get()._selectValidTeamId(
            teams,
            teamId || persistedTeamId || undefined,
          );

          set({
            teams,
            activeTeamId: finalTeamId ? parseInt(finalTeamId) : null,
          });

          get()._saveAuthData(data.access_token, authUserData, finalTeamId);
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to accept invitation';
          set({ error: errorMessage });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      checkInvitationStatus: async (token: string) => {
        try {
          set({ error: null });

          const response = await apiClient.get<{ expired: string }>(
            `/invitation/status/${token}`,
          );
          return response;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'Failed to check invitation status';
          set({ error: errorMessage });
          throw err;
        }
      },

      handleInvitation: async (
        teamId: string,
        token: string,
        status: 'accepted' | 'declined',
      ) => {
        try {
          set({ error: null });

          const response = await apiClient.post<{
            message?: string;
            detail?: string;
          }>('/invitation/accept', {
            team_id: teamId,
            status,
            token,
          });

          if (response.detail) {
            throw new Error(response.detail);
          }

          return response;
        } catch (err) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : `Failed to ${status} invitation`;
          set({ error: errorMessage });
          throw err;
        }
      },

      switchTeam: (teamId: number, teamsToValidateAgainst?: Team[]) => {
        const state = get();
        const teamsToCheck = teamsToValidateAgainst || state.teams;
        const teamExists = teamsToCheck.some((team) => team.id === teamId);

        if (!teamExists) {
          set({ error: 'Invalid team selection' });
          return;
        }

        set({ activeTeamId: teamId, error: null });
        secureStorage.setItem('team_id', teamId.toString());

        if (state.token) {
          apiClient.updateTeamId(teamId.toString());
        }
      },

      refreshTeams: async () => {
        try {
          const updatedTeams = await resolveAccessibleTeams();
          set({ teams: updatedTeams });
          return updatedTeams;
        } catch (err) {
          return get().teams;
        }
      },

      createTeam: async (data: { name: string }) => {
        try {
          set({ error: null });
          const newTeam = await apiClient.post<Team>('/team', {
            intent: 'create_team',
            team_name: data.name,
          });
          return newTeam;
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to create team';
          set({ error: errorMessage });
          throw err;
        }
      },

      setUser: (user) =>
        set({ user, isAuthenticated: !!user && !!get().token }),
      setToken: (token) =>
        set({ token, isAuthenticated: !!token && !!get().user }),
      setTeams: (teams) => set({ teams }),
      setActiveTeamId: (activeTeamId) => set({ activeTeamId }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }));

export const initializeAuth = async () => {
  const store = useAuthStore.getState();

  try {
    const storedToken = secureStorage.getItem('auth_token');
    const storedTeamId = secureStorage.getItem('team_id');

    if (storedToken) {
      apiClient.setAuth(storedToken, storedTeamId || undefined);

      store.setToken(storedToken);

      try {
        const userProfile = await apiClient.get<SettingsProfile>('/settings');
        const refreshedUserData = mapSettingsToUser(userProfile);
        const teams = await resolveAccessibleTeams();

        store.setUser(refreshedUserData);
        store.setTeams(teams);

        const validTeamId = store._selectValidTeamId(
          teams,
          storedTeamId || undefined,
        );
        store.setActiveTeamId(validTeamId ? parseInt(validTeamId) : null);

        if (validTeamId && validTeamId !== storedTeamId) {
          secureStorage.setItem('team_id', validTeamId);
          apiClient.updateTeamId(validTeamId);
        }
      } catch (refreshError) {
        store._clearAuthData();
      }
    }
  } catch (error) {
    store._clearAuthData();
  } finally {
    store.setLoading(false);
  }
};
